import React, { useState } from 'react';
import { 
    Box, 
    TextField, 
    Button, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel,
    CircularProgress,
    Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

const ParcelSearch = ({ map, cadastralLayer, onClose }) => {
    const [parcelNumber, setParcelNumber] = useState('');
    const [district, setDistrict] = useState('');
    const [sheet, setSheet] = useState('');
    const [plan, setPlan] = useState('');
    const [block, setBlock] = useState('');
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState('');
    const [markerLayer, setMarkerLayer] = useState(null);

    const districtOptions = [
        { code: '01', name: 'NICOSIA' },
        { code: '02', name: 'LIMASSOL' },
        { code: '03', name: 'LARNACA' },
        { code: '04', name: 'PAPHOS' },
        { code: '05', name: 'FAMAGUSTA' },
        { code: '06', name: 'KYRENIA' }
    ];

    const handleSearch = async () => {
        if (!parcelNumber.trim() && !sheet.trim() && !plan.trim() && !block.trim()) {
            setError('Please enter at least one search criterion');
            return;
        }

        setSearching(true);
        setError('');

        try {
            // Build query string
            let whereClause = [];
            
            if (parcelNumber.trim()) {
                whereClause.push(`PARCEL_NBR = '${parcelNumber.trim()}'`);
            }
            if (district) {
                whereClause.push(`DIST_CODE = '${district}'`);
            }
            if (sheet.trim()) {
                whereClause.push(`SHEET = '${sheet.trim()}'`);
            }
            if (plan.trim()) {
                whereClause.push(`PLAN_NBR = '${plan.trim()}'`);
            }
            if (block.trim()) {
                whereClause.push(`BLCK_CODE = '${block.trim()}'`);
            }

            const query = whereClause.join(' AND ');
            
            // Query the cadastral layer
            cadastralLayer.query()
                .where(query)
                .run((error, featureCollection) => {
                    setSearching(false);
                    
                    if (error) {
                        console.error('Search error:', error);
                        setError('Search failed. Please try again.');
                        return;
                    }

                    if (!featureCollection.features || featureCollection.features.length === 0) {
                        setError('No parcels found matching your criteria');
                        return;
                    }

                    // Clear previous marker
                    if (markerLayer) {
                        map.removeLayer(markerLayer);
                    }

                    // Get the first result
                    const feature = featureCollection.features[0];
                    const bounds = window.L.geoJSON(feature).getBounds();
                    
                    // Zoom to the parcel
                    map.fitBounds(bounds, { 
                        maxZoom: 18,
                        padding: [50, 50]
                    });

                    // Add a highlight marker
                    const highlightLayer = window.L.geoJSON(feature, {
                        style: {
                            color: '#ff7800',
                            weight: 3,
                            opacity: 1,
                            fillColor: '#ff7800',
                            fillOpacity: 0.2
                        }
                    }).addTo(map);

                    setMarkerLayer(highlightLayer);

                    // Show popup with parcel info
                    const props = feature.properties;
                    const districtMapping = {
                        '01': 'NICOSIA',
                        '02': 'LIMASSOL',
                        '03': 'LARNACA',
                        '04': 'PAPHOS',
                        '05': 'FAMAGUSTA',
                        '06': 'KYRENIA'
                    };

                    const popupContent = `
                        <div style="font-family: Arial, sans-serif; padding: 10px; min-width: 250px;">
                            <h3 style="margin: 0 0 10px 0; color: #ff7800; font-size: 16px; border-bottom: 2px solid #ff7800; padding-bottom: 5px;">
                                Search Result - Cadastral Parcel
                            </h3>
                            <table style="width: 100%; border-collapse: collapse;">
                                <tr style="border-bottom: 1px solid #e0e0e0;">
                                    <td style="padding: 8px 5px; font-weight: bold; color: #555;">District</td>
                                    <td style="padding: 8px 5px; color: #333;">${districtMapping[props.DIST_CODE] || props.DIST_CODE || ""}</td>
                                </tr>
                                <tr style="border-bottom: 1px solid #e0e0e0;">
                                    <td style="padding: 8px 5px; font-weight: bold; color: #555;">Sheet</td>
                                    <td style="padding: 8px 5px; color: #333;">${props.SHEET || ""}</td>
                                </tr>
                                <tr style="border-bottom: 1px solid #e0e0e0;">
                                    <td style="padding: 8px 5px; font-weight: bold; color: #555;">Plan</td>
                                    <td style="padding: 8px 5px; color: #333;">${props.PLAN_NBR || ""}</td>
                                </tr>
                                <tr style="border-bottom: 1px solid #e0e0e0;">
                                    <td style="padding: 8px 5px; font-weight: bold; color: #555;">Block</td>
                                    <td style="padding: 8px 5px; color: #333;">${props.BLCK_CODE || ""}</td>
                                </tr>
                                <tr>
                                    <td style="padding: 8px 5px; font-weight: bold; color: #555;">Parcel Number</td>
                                    <td style="padding: 8px 5px; color: #333;">${props.PARCEL_NBR || props.PARCEL || ""}</td>
                                </tr>
                            </table>
                            ${featureCollection.features.length > 1 ? 
                                `<div style="margin-top: 10px; padding: 8px; background: #fff3cd; border-radius: 4px; font-size: 12px; color: #856404;">
                                    Found ${featureCollection.features.length} matching parcels. Showing the first result.
                                </div>` : ''
                            }
                        </div>
                    `;

                    window.L.popup()
                        .setLatLng(bounds.getCenter())
                        .setContent(popupContent)
                        .openOn(map);

                    setError('');
                });

        } catch (err) {
            console.error('Search error:', err);
            setError('An error occurred during search');
            setSearching(false);
        }
    };

    const handleClear = () => {
        setParcelNumber('');
        setDistrict('');
        setSheet('');
        setPlan('');
        setBlock('');
        setError('');
        
        // Clear marker
        if (markerLayer) {
            map.removeLayer(markerLayer);
            setMarkerLayer(null);
        }
    };

    return (
        <Box
            sx={{
                position: 'absolute',
                top: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                padding: '20px',
                minWidth: '400px',
                maxWidth: '500px'
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <h3 style={{ margin: 0, color: '#2193b0', fontSize: '18px' }}>
                    Search Cadastral Parcels
                </h3>
                <Button
                    onClick={onClose}
                    size="small"
                    sx={{ minWidth: 'auto', padding: '4px' }}
                >
                    <CloseIcon />
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                    <InputLabel>District (Optional)</InputLabel>
                    <Select
                        value={district}
                        label="District (Optional)"
                        onChange={(e) => setDistrict(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>All Districts</em>
                        </MenuItem>
                        {districtOptions.map(d => (
                            <MenuItem key={d.code} value={d.code}>
                                {d.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="Parcel Number"
                    size="small"
                    fullWidth
                    value={parcelNumber}
                    onChange={(e) => setParcelNumber(e.target.value)}
                    placeholder="Enter parcel number"
                />

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        label="Sheet"
                        size="small"
                        fullWidth
                        value={sheet}
                        onChange={(e) => setSheet(e.target.value)}
                        placeholder="Optional"
                    />
                    <TextField
                        label="Plan"
                        size="small"
                        fullWidth
                        value={plan}
                        onChange={(e) => setPlan(e.target.value)}
                        placeholder="Optional"
                    />
                    <TextField
                        label="Block"
                        size="small"
                        fullWidth
                        value={block}
                        onChange={(e) => setBlock(e.target.value)}
                        placeholder="Optional"
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                        onClick={handleSearch}
                        disabled={searching}
                        sx={{
                            background: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #1a7a94 0%, #5bc4d8 100%)',
                            }
                        }}
                    >
                        {searching ? 'Searching...' : 'Search'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleClear}
                        disabled={searching}
                    >
                        Clear
                    </Button>
                </Box>
            </Box>

            <Box sx={{ mt: 2, fontSize: '12px', color: '#666' }}>
                <strong>Tip:</strong> Enter at least one search criterion. Combine multiple fields for more specific results.
            </Box>
        </Box>
    );
};

export default ParcelSearch;
